/** Total stars a game can award. */
export const MAX_STARS = 5;
/** Points scale per choice eliminated. */
export const POINTS_PER_STEP = 100;

export interface LevelScore {
  points: number;
  possiblePoints: number;
}

/**
 * Score for one answered level (a list of `choices` options).
 * `guessesUsed` counts every guess including the final correct one, so a
 * first-try answer earns the full `possiblePoints` and each miss costs 100.
 */
export function levelScore(choices: number, guessesUsed: number): LevelScore {
  if (!Number.isInteger(choices) || choices < 1) throw new RangeError(`choices must be >= 1, got ${choices}`);
  if (!Number.isInteger(guessesUsed) || guessesUsed < 1 || guessesUsed > choices) {
    throw new RangeError(`guessesUsed must be in 1..${choices}, got ${guessesUsed}`);
  }
  return {
    points: POINTS_PER_STEP * (choices - guessesUsed),
    possiblePoints: POINTS_PER_STEP * (choices - 1),
  };
}

/**
 * Score thresholds for stars. Index i is the score at which star i+1 is
 * *lost* — i.e. you have i+1 stars while score is below thresholds[i+1].
 * Thresholds land at 0, 50%, 75%, 87.5% and 100% of the possible score,
 * rounded to the nearest 100 points.
 */
export function starThresholds(possible: number, stars = MAX_STARS): number[] {
  return Array.from({ length: stars }, (_, i) =>
    i === stars - 1 ? possible : POINTS_PER_STEP * Math.round((possible - possible / 2 ** i) / POINTS_PER_STEP),
  );
}

/** 1 star minimum, 5 for a perfect run. */
export function starsFor(score: number, possible: number, stars = MAX_STARS): number {
  const idx = starThresholds(possible, stars).findIndex((t) => t > score);
  return idx < 0 ? stars : idx;
}
